import { UserInfo, ConversationRequest, ChatMessage, ApplicationInfo, FrontendSettings, Language, Profile, defaultProfile } from "./models";

export const frontendSettings = async (): Promise<Response | FrontendSettings | null> => {
    const response = await fetch("/frontend_settings", {
        method: "GET",
    }).then((res) => {
        return res.json()
    }).catch((err) => {
        console.error("There was an issue fetching your data.");
        return null
    })

    return response
}

export async function conversationApi(options: ConversationRequest, abortSignal: AbortSignal): Promise<Response> {
    const response = await fetch("/conversation", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            messages: options.messages,
            lang: options.lang,
            province: options.province,
            index: options.index,
            region: options.region,
            profileId: options.profileId
        }),
        signal: abortSignal
    });

    return response;
}

export async function getUserInfo(): Promise<UserInfo[]> {
    const response = await fetch('/.auth/me');
    if (!response.ok) {
        console.log("No identity provider found. Access to chat will be blocked.")
        return [];
    }

    const payload = await response.json();
    return payload;
}

// Optional PingID session (see backend/auth/pingid_routes.py). Returns the same
// UserInfo[] shape as `/.auth/me` so callers can treat both providers uniformly.
export async function getPingIdUserInfo(): Promise<UserInfo[]> {
    const response = await fetch('/api/v1/auth/me');
    if (!response.ok) {
        return [];
    }

    const payload = await response.json();
    return payload;
}

export async function pingIdLogout(): Promise<Response> {
    return fetch('/api/v1/auth/logout', { method: "POST" });
}

export type LoginDetectResult = {
    redirect_url?: string;
    error?: string;
};

// Given an email address, asks the backend which IdP it belongs to
// (backend/auth/provider_detector.py: telus.com -> PingID, telushealth.com -> Azure AD)
// and where to send the browser to sign in there.
export async function detectLoginProvider(email: string): Promise<LoginDetectResult> {
    const response = await fetch(`/api/v1/auth/login/detect?email=${encodeURIComponent(email)}`);
    return response.json();
}

export async function DownloadFileApi(url: string): Promise<Response> {
    const response = await fetch("/downloadfile", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            url: url
        })
    });
    return response;    
}

export const historyClear = async (convId: string, lang: Language) : Promise<Response> => {
    const response = await fetch(`/history/clear?lang=${lang}`, {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
        }),
        headers: {
            "Content-Type": "application/json"
        },
    })
    .then((res) => {
        return res
    })
    .catch((err) => {
        console.error("There was an issue fetching your data.");
        let errRes: Response = {
            ...new Response,
            ok: false,
            status: 500,
        }
        return errRes;
    })
    return response;
}

export async function getGreetingMessage(lang: Language): Promise<ChatMessage> {
    const response = await fetch(`/getGreetingMessage?lang=${lang}`);
    if (!response.ok) {
        console.log("No welcome message configured.")
        return {role:'system', content:'', id:'',date:''};
    }

    const messages = await response.json();
        
    return messages;
}

export async function getApplicationInfo(lang: Language): Promise<ApplicationInfo> {
    const response = await fetch(`/applicationInfo?lang=${lang}`);
    if (!response.ok) {
        return {role:'system', allowAnonymousUser:false};
    }

    const messages = await response.json();
        
    return messages;
}

export async function getTranslations(lang: Language): Promise<Record<string, string>> {
    const response = await fetch(`/getTranslations?lang=${lang}`);
    if (!response.ok) {
        console.error("Failed to fetch translations.");
        return {};
    }

    const translations = await response.json();
    return translations;
}

export async function getChatContexts(lang: Language): Promise<[{}]> { 
    const response = await fetch(`/getChatContexts?lang=${lang}`);
    if (!response.ok) {
        console.error("Failed to fetch context.");
        return [{}];
    }
    const contexts = await response.json();
    return contexts;  
}

export async function getSuggestedPrompts(lang: Language): Promise<string[]> {
    const response = await fetch(`/getSuggestedPrompt?lang=${lang}`);
    if (!response.ok) {
        console.error("Failed to fetch suggested prompts.");
        return [];
    }
    const suggestedPrompts = await response.json();
    return suggestedPrompts.suggested_prompts
}export async function getProfile(profileId: string,lang: string): Promise<Profile>  { 
    const response = await fetch(`/profile?profileId=${profileId}&lang=${lang}`);
    if (!response.ok) {
        console.error("Failed to fetch pofile.");
        return defaultProfile;
    }
    const data = await response.json();

    const profile: Profile = {
        clientId: data.clientId || "",
        displayName: data.displayName || "",
        primaryColor: data.primaryColor || "#4B286D",
        appUrl: data.appUrl || "",
    };

    return profile;
}
